import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import ContactPopup from './ContactPopup';
import { Button } from '@/components/ui/button';
import { openChat } from '@/utils/chatService';

const Header = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMegaMenu, setOpenMegaMenu] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('trademark');
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileMediaOpen, setMobileMediaOpen] = useState(false);
  const [mobileServiceCategory, setMobileServiceCategory] = useState(null);

  const megaMenuData = {
    trademark: {
      title: 'Trademark Services',
      items: [
        { name: 'Register Your Trademark', path: '/services/trademark-registration' },
        { name: 'Clearance Search Services', path: '/services/clearance-search' },
        { name: 'Trademark Ownership Transfers', path: '/services/ownership-transfers' },
        { name: 'Submission of Statements of Use', path: '/services/statement-of-use' },
        { name: 'Support and Response for Trademark Office Actions', path: '/services/office-actions' },
        { name: 'Active Monitoring for Trademark Infringement', path: '/services/trademark-monitoring' },
        { name: 'File TTAB Petition', path: '/services/ttab-petition' },
      ]
    },
    company: {
      title: 'Company Formation Services',
      items: [
        { name: 'Limited Liability Company', path: '/services/llc' },
        { name: 'C Corporation', path: '/services/c-corporation' },
        { name: 'S Corporation', path: '/services/s-corporation' },
        { name: 'Non-Profit', path: '/services/non-profit' },
      ]
    },
    copyright: {
      title: 'Copyright Services',
      items: [
        { name: 'Register Your Copyright', path: '/services/register-copyright' },
        { name: 'Assistance for Your Copyright Enforcement', path: '/services/copyright-enforcement' },
        { name: 'Copyright Transfers and Licensing Support', path: '/services/copyright-transfers' },
        { name: 'Legal Representation for Your Copyright', path: '/services/copyright-legal' },
        { name: 'Management of Your Copyright Portfolio', path: '/services/copyright-portfolio' },
        { name: 'Compliance with DMCA Act', path: '/services/dmca-compliance' },
      ]
    },
    patent: {
      title: 'Patent Services',
      items: [
        { name: 'Search for Existing Patent', path: '/services/patent-search' },
        { name: 'Support and Response for Patent Office Actions', path: '/services/patent-office-actions' },
        { name: 'Application Submission for Patent', path: '/services/patent-application' },
        { name: 'Legal Representation for Patent Protection', path: '/services/patent-legal' },
      ]
    },
    legal: {
      title: 'Legal Services',
      items: [
        { name: 'Business Immigration Law', path: '/services/immigration-law' },
        { name: 'Due Diligence Process', path: '/services/due-diligence' },
        { name: 'IT & Commercial Law', path: '/services/it-commercial-law' },
        { name: 'Legal Services on Partnerships & LLPs', path: '/services/partnership-llp' },
        { name: 'Data Protection & Privacy Law', path: '/services/data-protection' },
      ]
    },
    corporate: {
      title: 'Additional Corporate Services',
      items: [
        { name: 'Amazon Brand Registry in the US', path: '/services/amazon-brand-registry' },
        { name: 'Bookkeeping Service', path: '/services/bookkeeping' },
        { name: 'Business Formation Services', path: '/services/business-formation' },
        { name: 'Certificate of Good Standing', path: '/services/good-standing' },
        { name: 'Company Dissolution', path: '/services/company-dissolution' },
        { name: 'Company Name Change', path: '/services/company-name-change' },
        { name: 'Company Renewal Services', path: '/services/company-renewal' },
        { name: 'Issue Of Shares', path: '/services/issue-shares' },
        { name: 'Nominee Director & Secretarial Services', path: '/services/nominee-director' },
        { name: 'Tax Advisory & Filing', path: '/services/tax-advisory' },
        { name: 'Transfer of Shares', path: '/services/transfer-shares' },
        { name: 'CFO Services', path: '/services/cfo-services' },
      ]
    },
  };

  return (
    <>
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/assets/img/brand/logo-dark.png" 
                alt="Trademark Scribe" 
                className="h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-red-600 font-medium transition-colors">
                Home
              </Link>
              <Link to="/about" className="text-gray-700 hover:text-red-600 font-medium transition-colors">
                About Us
              </Link>
              
              {/* Services Mega Menu */}
              <div 
                className="relative"
                onMouseEnter={() => {
                  setOpenMegaMenu('services');
                  setSelectedCategory('trademark');
                }}
                onMouseLeave={() => setOpenMegaMenu(null)}
              >
                <button className="flex items-center space-x-1 text-gray-700 hover:text-red-600 font-medium transition-colors">
                  <span>Services</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {openMegaMenu === 'services' && (
                  <div className="absolute left-1/2 transform -translate-x-1/2 top-full pt-2 w-screen max-w-5xl">
                    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
                      <div className="grid grid-cols-12">
                        {/* Left Sidebar - Categories */}
                        <div className="col-span-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
                          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">Service Categories</h3>
                          <div className="space-y-2">
                            {Object.entries(megaMenuData).map(([key, category]) => (
                              <button
                                key={key}
                                onMouseEnter={() => setSelectedCategory(key)}
                                className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                                  selectedCategory === key
                                    ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-lg'
                                    : 'text-gray-300 hover:bg-white/10 hover:text-white'
                                }`}
                              >
                                <span className="font-medium text-sm">{category.title}</span>
                              </button>
                            ))}
                          </div>
                          
                          {/* Footer CTA */}
                          <div className="mt-8 pt-6 border-t border-white/10">
                            <button
                              onClick={() => {
                                openChat();
                                setOpenMegaMenu(null);
                              }}
                              className="w-full bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-lg text-sm font-medium transition-all backdrop-blur-sm border border-white/20"
                            >
                              Get Free Consultation
                            </button>
                          </div>
                        </div>

                        {/* Right Content - Service Items */}
                        <div className="col-span-8 p-8 bg-white max-h-[500px] overflow-y-auto">
                          {megaMenuData[selectedCategory] && (
                            <div className="space-y-6">
                              <div>
                                <h4 className="text-2xl font-bold text-gray-900 mb-2">{megaMenuData[selectedCategory].title}</h4>
                                <div className="h-1 w-20 bg-gradient-to-r from-red-600 to-orange-500 rounded-full"></div>
                              </div>
                              
                              <div className="grid grid-cols-1 gap-2">
                                {megaMenuData[selectedCategory].items.map((item, itemIdx) => (
                                  <Link
                                    key={itemIdx}
                                    to={item.path}
                                    className="group flex items-center justify-between px-4 py-3 rounded-lg hover:bg-red-50 transition-all border border-transparent hover:border-red-100"
                                    onClick={() => setOpenMegaMenu(null)}
                                  >
                                    <span className="text-gray-700 group-hover:text-red-600 font-medium text-sm transition-colors">
                                      {item.name}
                                    </span>
                                    <svg 
                                      className="w-4 h-4 text-gray-400 group-hover:text-red-600 transform group-hover:translate-x-1 transition-all" 
                                      fill="none" 
                                      viewBox="0 0 24 24" 
                                      stroke="currentColor"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Media Center Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setOpenMegaMenu('media')}
                onMouseLeave={() => setOpenMegaMenu(null)}
              >
                <button className="flex items-center space-x-1 text-gray-700 hover:text-red-600 font-medium transition-colors">
                  <span>Media Center</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {openMegaMenu === 'media' && (
                  <div className="absolute left-1/2 transform -translate-x-1/2 top-full pt-2 w-64">
                    <div className="bg-white rounded-lg shadow-2xl py-4 border border-gray-100">
                      <Link
                        to="/media/blogs"
                        className="block px-6 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                        onClick={() => setOpenMegaMenu(null)}
                      >
                        Blogs
                      </Link>
                      <Link
                        to="/media/news-updates"
                        className="block px-6 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                        onClick={() => setOpenMegaMenu(null)}
                      >
                        News & Updates
                      </Link>
                      <Link
                        to="/media/publications"
                        className="block px-6 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                        onClick={() => setOpenMegaMenu(null)}
                      >
                        Publications
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <Link to="/contact" className="text-gray-700 hover:text-red-600 font-medium transition-colors">
                Contact Us
              </Link>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <Button
                onClick={() => setIsPopupOpen(true)}
                className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-6 py-2 rounded-full font-semibold hover:shadow-lg"
                data-testid="header-get-started-btn"
              >
                Get Started
              </Button>
              <Button
                onClick={() => openChat()}
                variant="outline"
                className="border-2 border-red-600 text-red-600 px-6 py-2 rounded-full font-semibold hover:bg-red-50"
                data-testid="header-chat-now-btn"
              >
                Chat Now
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-gray-700"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden py-4 border-t max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-menu">
              <div className="space-y-2">
                <Link to="/" className="block text-gray-700 hover:text-red-600 py-2 px-4" onClick={() => setIsMobileMenuOpen(false)}>
                  Home
                </Link>
                <Link to="/about" className="block text-gray-700 hover:text-red-600 py-2 px-4" onClick={() => setIsMobileMenuOpen(false)}>
                  About Us
                </Link>

                {/* Mobile Services Accordion */}
                <div>
                  <button
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className="w-full flex items-center justify-between text-gray-700 hover:text-red-600 py-2 px-4 font-medium"
                  >
                    <span>Services</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {mobileServicesOpen && (
                    <div className="pl-4 space-y-2 mt-2">
                      {Object.entries(megaMenuData).map(([key, category]) => (
                        <div key={key}>
                          <button
                            onClick={() => setMobileServiceCategory(mobileServiceCategory === key ? null : key)}
                            className="w-full text-left text-gray-600 hover:text-red-600 py-2 px-4 text-sm font-medium flex items-center justify-between"
                          >
                            <span>{category.title}</span>
                            <ChevronDown className={`w-3 h-3 transition-transform ${mobileServiceCategory === key ? 'rotate-180' : ''}`} />
                          </button>
                          
                          {mobileServiceCategory === key && (
                            <div className="pl-4 space-y-1 mt-1">
                              {category.items.map((item, idx) => (
                                <Link
                                  key={idx}
                                  to={item.path}
                                  className="block text-gray-600 hover:text-red-600 py-2 px-4 text-sm"
                                  onClick={() => {
                                    setIsMobileMenuOpen(false);
                                    setMobileServicesOpen(false);
                                    setMobileServiceCategory(null);
                                  }}
                                >
                                  {item.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mobile Media Center Accordion */}
                <div>
                  <button
                    onClick={() => setMobileMediaOpen(!mobileMediaOpen)}
                    className="w-full flex items-center justify-between text-gray-700 hover:text-red-600 py-2 px-4 font-medium"
                  >
                    <span>Media Center</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileMediaOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {mobileMediaOpen && (
                    <div className="pl-4 space-y-1 mt-2">
                      <Link
                        to="/media/blogs"
                        className="block text-gray-600 hover:text-red-600 py-2 px-4 text-sm"
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          setMobileMediaOpen(false);
                        }}
                      >
                        Blogs
                      </Link>
                      <Link
                        to="/media/news-updates"
                        className="block text-gray-600 hover:text-red-600 py-2 px-4 text-sm"
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          setMobileMediaOpen(false);
                        }}
                      >
                        News & Updates
                      </Link>
                      <Link
                        to="/media/publications"
                        className="block text-gray-600 hover:text-red-600 py-2 px-4 text-sm"
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          setMobileMediaOpen(false);
                        }}
                      >
                        Publications
                      </Link>
                    </div>
                  )}
                </div>

                <Link to="/contact" className="block text-gray-700 hover:text-red-600 py-2 px-4" onClick={() => setIsMobileMenuOpen(false)}>
                  Contact Us
                </Link>
              </div>

              <div className="pt-4 px-4 space-y-2">
                <Button
                  onClick={() => { setIsPopupOpen(true); setIsMobileMenuOpen(false); }}
                  className="w-full bg-gradient-to-r from-red-600 to-orange-500 text-white"
                  data-testid="mobile-get-started-btn"
                >
                  Get Started
                </Button>
                <Button
                  onClick={() => { openChat(); setIsMobileMenuOpen(false); }}
                  variant="outline"
                  className="w-full border-2 border-red-600 text-red-600"
                  data-testid="mobile-chat-now-btn"
                >
                  Chat Now
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default Header;